// Subscription utility functions
import API_BASE_URL from '../Config/config';

export const SUBSCRIPTION_CONFIG = {
  TRIAL_DAYS: 7,
  SUBSCRIPTION_DAYS: 90,
  SUBSCRIPTION_PRICE: 2999
};

const MS_IN_DAY = 24 * 60 * 60 * 1000;

const resolveUserCreationDate = (userId) => {
  try {
    const rawUser = localStorage.getItem('user');
    if (rawUser) {
      const user = JSON.parse(rawUser);
      const created =
        user?.created_at ??
        user?.createdAt ??
        user?.createdOn ??
        user?.createdDate ??
        user?.created;
      if (created) {
        const parsed = new Date(created);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed;
        }
      }
    }
  } catch (err) {
    console.warn('Unable to parse user creation date:', err?.message);
  }

  // fallback: remember the first time we initialized the trial
  const fallbackKey = `user_trial_initialized_at_${userId}`;
  const storedFallback = localStorage.getItem(fallbackKey);
  if (storedFallback) {
    const parsed = new Date(storedFallback);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  const now = new Date();
  localStorage.setItem(fallbackKey, now.toISOString());
  return now;
};

const ensureTrialRecord = (userId) => {
  const storageKey = `user_trial_started_${userId}`;
  const existing = localStorage.getItem(storageKey);

  if (existing) {
    try {
      const parsed = JSON.parse(existing);
      if (parsed && parsed.startDate && parsed.endDate) {
        return parsed;
      }
    } catch {
      // fallthrough to re-initialize
    }
  }

  const startDate = resolveUserCreationDate(userId);
  const now = new Date();
  const normalizedStart =
    startDate && !Number.isNaN(startDate.getTime()) && startDate <= now
      ? startDate
      : now;

  const endDate = new Date(normalizedStart.getTime() + SUBSCRIPTION_CONFIG.TRIAL_DAYS * MS_IN_DAY);
  const trial = {
    startDate: normalizedStart.toISOString(),
    endDate: endDate.toISOString()
  };

  localStorage.setItem(storageKey, JSON.stringify(trial));
  return trial;
};

export const checkTrialStatus = async (userId) => {
  if (!userId) {
    return {
      isTrialActive: false,
      daysLeft: 0,
      isExpired: true
    };
  }

  // First check backend database for trial status by checking payments
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    // Check payments to find active trial
    const paymentsResponse = await fetch(`${API_BASE_URL}/api/skillgenie-premium/user/${userId}`, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    
    if (paymentsResponse.ok) {
      const paymentsData = await paymentsResponse.json();
      if (paymentsData.ok && paymentsData.payments && Array.isArray(paymentsData.payments)) {
        const now = new Date();
        
        // Find active trial payment
        const activeTrial = paymentsData.payments.find(p => {
          if (p.status !== 'success' || p.payment_method !== 'trial') return false;
          
          // Check if trial is still active
          if (p.end_date) {
            const endDate = new Date(p.end_date);
            return endDate > now;
          }
          
          // If no end_date but created recently, consider active
          if (p.created_at) {
            const createdAt = new Date(p.created_at);
            const daysSinceCreation = (now - createdAt) / (1000 * 60 * 60 * 24);
            return daysSinceCreation < 7;
          }
          
          return false;
        });
        
        if (activeTrial) {
          const startDate = activeTrial.start_date ? new Date(activeTrial.start_date) : new Date(activeTrial.created_at);
          const endDate = activeTrial.end_date ? new Date(activeTrial.end_date) : new Date(startDate.getTime() + 7 * MS_IN_DAY);
          const diffMs = endDate - now;
          const daysLeft = Math.max(0, Math.ceil(diffMs / MS_IN_DAY));
          const daysUsed = Math.floor((now - startDate) / MS_IN_DAY);
          
          // Sync with localStorage
          const trialData = {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          };
          localStorage.setItem(`user_trial_started_${userId}`, JSON.stringify(trialData));
          
          return {
            isTrialActive: daysLeft > 0,
            daysLeft,
            isExpired: daysLeft === 0,
            startDate: startDate.toISOString(),
            daysUsed,
            endDate: endDate.toISOString(),
            source: 'database'
          };
        }
      }
    }
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.warn('⚠️ Trial status API unavailable, using localStorage fallback:', error.message);
    }
  }

  // Fallback to localStorage
  const trialData = ensureTrialRecord(userId);
  const now = new Date();
  const startDate = new Date(trialData.startDate);
  const endDate = new Date(trialData.endDate);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    // corrupt entry, reset
    localStorage.removeItem(`user_trial_started_${userId}`);
    return await checkTrialStatus(userId);
  }

  const clampedEnd = endDate < startDate ? new Date(startDate.getTime() + SUBSCRIPTION_CONFIG.TRIAL_DAYS * MS_IN_DAY) : endDate;
  const isExpired = now >= clampedEnd;
  const diffMs = clampedEnd - now;
  const daysLeft = isExpired ? 0 : Math.ceil(diffMs / MS_IN_DAY);
  const daysUsed = Math.floor((now - startDate) / MS_IN_DAY);

  return {
    isTrialActive: !isExpired,
    daysLeft,
    isExpired,
    startDate,
    daysUsed,
    endDate: clampedEnd,
    source: 'localStorage'
  };
};

export const checkSubscriptionStatus = async (userId) => {
  // First check database for active subscription or trial using SkillGenie premium API
  if (userId) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
      
      const encodedUserId = encodeURIComponent(userId);
      const response = await fetch(`${API_BASE_URL}/api/skillgenie-premium/status/${encodedUserId}`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const dbData = await response.json();
          
          // Check for active subscription (non-trial)
          if (dbData.ok && dbData.hasActiveSubscription && dbData.subscription) {
            const subscription = dbData.subscription;
            
            // Only return subscription if it's not a trial
            if (subscription.payment_method !== 'trial' && subscription.amount !== 0) {
              return {
                isSubscribed: true,
                isValid: true,
                daysLeft: subscription.daysRemaining,
                isExpired: false,
                subscriptionStart: subscription.start_date,
                subscriptionEnd: subscription.end_date,
                amount: subscription.amount,
                paymentMethod: subscription.payment_method,
                planName: subscription.plan_name,
                source: 'database'
              };
            }
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('⚠️ Subscription API timeout - using localStorage fallback');
      } else {
        console.warn('⚠️ Subscription API unavailable (502/CORS) - using localStorage fallback:', error.message);
      }
      // Fallback to localStorage without throwing error
    }
  }

  // Fallback to localStorage
  const subscriptionDataKey = `subscription_data_${userId}`;
  const subscriptionData = localStorage.getItem(subscriptionDataKey);
  if (!subscriptionData) {
    return {
      isSubscribed: false,
      isValid: false,
      daysLeft: 0,
      isExpired: true,
      source: 'localStorage'
    };
  }

  try {
    const subscription = JSON.parse(subscriptionData);
    const now = new Date();
    const subscriptionEnd = new Date(subscription.subscriptionEnd);
    const isExpired = now > subscriptionEnd;
    const diffTime = subscriptionEnd - now;
    const daysLeft = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));

    return {
      isSubscribed: subscription.isSubscribed,
      isValid: !isExpired,
      daysLeft,
      isExpired,
      subscriptionStart: subscription.subscriptionStart,
      subscriptionEnd: subscription.subscriptionEnd,
      amount: subscription.amount,
      source: 'localStorage'
    };
  } catch (error) {
    console.error('Error parsing subscription data:', error);
    return {
      isSubscribed: false,
      isValid: false,
      daysLeft: 0,
      isExpired: true,
      source: 'localStorage'
    };
  }
};

export const hasAccess = async (userId) => {
  const trialStatus = await checkTrialStatus(userId);
  const subscriptionStatus = await checkSubscriptionStatus(userId);

  // If user has valid subscription, they have access
  if (subscriptionStatus.isSubscribed && subscriptionStatus.isValid) {
    return {
      hasAccess: true,
      accessType: 'subscription',
      daysLeft: subscriptionStatus.daysLeft,
      message: `Premium access valid for ${subscriptionStatus.daysLeft} days`,
      source: subscriptionStatus.source
    };
  }

  // If trial is still active, they have access
  if (trialStatus.isTrialActive) {
    return {
      hasAccess: true,
      accessType: 'trial',
      daysLeft: trialStatus.daysLeft,
      message: `Free trial - ${trialStatus.daysLeft} days remaining`,
      source: 'localStorage'
    };
  }

  // No access - trial expired and no subscription
  return {
    hasAccess: false,
    accessType: 'expired',
    daysLeft: 0,
    message: 'Access expired. Please subscribe to continue.',
    source: 'localStorage'
  };
};

export const getSubscriptionSummary = async (userId) => {
  const trialStatus = await checkTrialStatus(userId);
  const subscriptionStatus = await checkSubscriptionStatus(userId);
  const accessStatus = await hasAccess(userId);

  return {
    ...accessStatus,
    trial: trialStatus,
    subscription: subscriptionStatus,
    needsPayment: !accessStatus.hasAccess
  };
};

export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const formatTimeLeft = (daysLeft) => {
  if (daysLeft === 0) return 'Expires today';
  if (daysLeft === 1) return 'Expires tomorrow';
  return `${daysLeft} days remaining`;
};



